import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Heart, ChevronLeft, ChevronRight } from 'lucide-react';

const Hero = React.memo(() => {
  const [currentSlide, setCurrentSlide] = useState(0);

  /* ✅ Memoized slides (created once) */
  const bannerImages = useMemo(
    () => [
      {
        url: 'images/banner/b2.jpeg',
        title: 'Compassionate Healthcare Support',
        description:
          'Providing medical beds and equipment for bedridden patients in need',
      },
      {
        url: 'images/banner/b1.jpeg',
        title: 'Compassionate Elderly Care',
        description:
          'Supporting seniors with mobility assistance and professional home care to ensure safety, comfort, and dignity.',
      },
      {
        url: 'images/banner/b3.jpeg',
        title: 'Mobility for All',
        description:
          'Providing wheelchairs and mobility aids to enhance independence',
      },
      {
        url: 'images/banner/b4.jpeg',
        title: 'Essential Medical Equipment Support',
        description:
          'Providing life-saving medical devices and oxygen support to patients receiving treatment at home.',
      },
    ],
    []
  );

  /* ✅ Stable auto-slide interval */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerImages.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [bannerImages.length]);

  /* ✅ Memoized handlers */
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % bannerImages.length);
  }, [bannerImages.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(
      (prev) => (prev - 1 + bannerImages.length) % bannerImages.length
    );
  }, [bannerImages.length]);

  const scrollToSection = useCallback((sectionId) => {
    const target = document.getElementById(sectionId);
    if (!target || !window.lenis) return;

    window.lenis.scrollTo(target, {
      duration: 1.6,
      easing: (t) => 1 - Math.pow(1 - t, 4),
    });
  }, []);

  return (
    <section id="home" className="relative h-screen overflow-hidden">
      {/* Background Slider */}
      <div className="absolute inset-0">
        {bannerImages.map((image, index) => {
          const isActive = index === currentSlide;

          return (
            <div
              key={image.url}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                isActive ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image.url}
                alt={image.title}
                loading={isActive ? 'eager' : 'lazy'}
                decoding="async"
                className="w-full h-full object-cover will-change-transform"
              />
              <div className="absolute inset-0 bg-black/40" />
            </div>
          );
        })}
      </div>

      {/* Navigation */}
      <button
        onClick={prevSlide}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-20
                   bg-white/20 hover:bg-white/30 text-white
                   p-3 rounded-full backdrop-blur-md transition"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-6 top-1/2 -translate-y-1/2 z-20
                   bg-white/20 hover:bg-white/30 text-white
                   p-3 rounded-full backdrop-blur-md transition"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Indicators */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {bannerImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition ${
              index === currentSlide ? 'bg-white' : 'bg-white/40'
            }`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 flex items-center justify-center h-full px-6">
        <div className="text-center text-white max-w-4xl">
          <Heart className="h-16 w-16 mx-auto mb-6 opacity-90" />

          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            {bannerImages[currentSlide].title}
          </h1>

          <p className="text-lg md:text-2xl mb-10 opacity-90">
            {bannerImages[currentSlide].description}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => scrollToSection('about')}
              className="bg-blue-600 hover:bg-blue-700 px-10 py-4 rounded-full
                         text-lg font-semibold transition-transform hover:scale-105"
            >
              Learn More
            </button>

            <button
              onClick={() => scrollToSection('contact')}
              className="bg-white/20 hover:bg-white/30 px-10 py-4 rounded-full
                         text-lg font-semibold backdrop-blur-md border border-white/40
                         transition-transform hover:scale-105"
            >
              Get Involved
            </button>
          </div>
        </div>
      </div>
    </section>
  );
});

export default Hero;
